const inFlightByFile = new Map<string, Map<number, number>>();

/**
 * Count of upload-flow server round-trips in flight right now (session create,
 * init, part-URL signing, complete, reconcile). These do real upload work but
 * move no bytes, so without counting them the throughput detector would wrongly
 * flag the normal gap between parts/files as "stalled".
 */
let activeRequestCount = 0;

/**
 * Marks an upload-flow server request as in flight for its duration, so the
 * stall detector treats the upload as working even while no bytes are moving.
 */
export async function trackUploadRequest<T>(work: Promise<T>): Promise<T> {
  activeRequestCount += 1;
  try {
    return await work;
  } finally {
    activeRequestCount = Math.max(0, activeRequestCount - 1);
  }
}

export function setPartProgress(
  clientFileKey: string,
  partNumber: number,
  loadedBytes: number,
) {
  let parts = inFlightByFile.get(clientFileKey);
  if (!parts) {
    parts = new Map();
    inFlightByFile.set(clientFileKey, parts);
  }
  parts.set(partNumber, loadedBytes);
}

export function clearPartProgress(clientFileKey: string, partNumber: number) {
  const parts = inFlightByFile.get(clientFileKey);
  if (!parts) return;
  parts.delete(partNumber);
  if (parts.size === 0) {
    inFlightByFile.delete(clientFileKey);
  }
}

export function clearFileProgress(clientFileKey: string) {
  inFlightByFile.delete(clientFileKey);
}

export function clearAllLiveProgress() {
  inFlightByFile.clear();
}

export function getLiveBytesForFile(clientFileKey: string) {
  const parts = inFlightByFile.get(clientFileKey);
  if (!parts) return 0;
  return Array.from(parts.values()).reduce((sum, loaded) => sum + loaded, 0);
}

export function hasInFlightUploads() {
  return inFlightByFile.size > 0 || activeRequestCount > 0;
}
